import { Schema } from "effect";
import { RunId } from "./ids.ts";

// `AgentTurnEvent` (ADR-0038 §2) — the wire format for one generative agent turn. Unlike
// `HarnessDelta` (`packages/ports/src/agent-harness.ts`, in-process only — `runStreaming`
// never crosses a wire, so it stays a plain `_tag` union), these events ARE the wire
// contract: Unit 6's session-backed route `Schema`-encodes them one-per-line to the web
// client, which decodes through this SAME `Schema` (R4 — parse, don't validate; drift
// becomes a decode-time failure at either end, never a silent mismatch). Tags are
// kebab-case, matching `session.ts`'s `RunSessionEvent` vocabulary (CONTEXT.md, OQ4): the
// two unions describe one run from two angles — the durable per-event record vs. the live
// per-turn wire stream — so they read as one vocabulary, not two invented in parallel.
//
// Deliberate deviation from the plan's §2 sketch: `result` carries no `ConversationRef`.
// The session pointer is already durably recorded as the run's `session-pointer` session
// event (Unit 2, `session.ts`'s `SessionPointer`); Unit 6's route resolves the next turn's
// resume handle from `SessionLog`, not from a field on this wire event. Carrying a
// transcript-bearing ref (a raw vendor session id plus a rendered transcript) on a
// browser-facing event would leak server-internal session state the browser has no use
// for — it never resumes a session itself, the server does.

// Assistant prose — token deltas the consumer concatenates to render the turn's visible
// narration (fast-edit turns emit none of these at all, by design — see agent-service.ts).
export const Prose = Schema.TaggedStruct("prose", {
  text: Schema.String,
});
export type Prose = typeof Prose.Type;

// Agent activity the UI may show subordinately (a "thinking…" strip, not the main
// narration channel) — mapped from the harness's `thinking-delta`.
export const Status = Schema.TaggedStruct("status", {
  text: Schema.String,
});
export type Status = typeof Status.Type;

// A tool-activity heartbeat: `name` is the (unprefixed) tool name, `phase` distinguishes
// the call from its result so the UI can render a spinner-then-settle affordance without
// waiting for the full result payload.
export const ToolActivity = Schema.TaggedStruct("tool", {
  name: Schema.String,
  phase: Schema.Literal("call", "result"),
});
export type ToolActivity = typeof ToolActivity.Type;

// The turn's terminal event — exactly one per turn, mirroring `session.ts`'s
// `RunFinished`. `outcome` is `"completed"` when the SDK's terminal result was `success`;
// `"failed"` for every other result subtype (an idle-timeout or harness failure reaches
// the stream's failure channel instead, never this event — see `AgentRunFailed`).
export const TurnResult = Schema.TaggedStruct("result", {
  outcome: Schema.Literal("completed", "failed"),
  reason: Schema.optional(Schema.String),
});
export type TurnResult = typeof TurnResult.Type;

// A keepalive on an otherwise-silent stream (Unit 6, carried over from the deleted NDJSON routes'
// `{t:"ping"}`): Bun.serve kills a request after 10s of socket idleness, and a silent model call
// (planning, a slow tool round) can easily outlast that. The client ignores this tag entirely — it
// exists only to keep the socket warm.
export const Ping = Schema.TaggedStruct("ping", {});
export type Ping = typeof Ping.Type;

// The turn's stream FAILED (a dormant agent, an idle-timeout, a harness defect) — `AgentRunFailed`'s
// reason, reified as a frame because the `Stream`'s failure channel cannot cross the HTTP wire (Unit
// 6, the deleted NDJSON routes' `{t:"error"}` analogue). Exactly one, then the response body ends;
// unlike `TurnResult` (a normal terminal event on a stream that completed), this is what the route
// emits when `Agent.turn`'s stream itself fails rather than reaching its own `result` event.
export const TurnError = Schema.TaggedStruct("error", {
  reason: Schema.String,
});
export type TurnError = typeof TurnError.Type;

export const AgentTurnEvent = Schema.Union(
  Prose,
  Status,
  ToolActivity,
  TurnResult,
  Ping,
  TurnError,
);
export type AgentTurnEvent = typeof AgentTurnEvent.Type;

// The conversation message projection (Unit 3, D7 — "one projection, typed parts"): a
// `ConversationMessage` keeps every part kind present in the data so the SAME projection serves two
// renderers with different part-render policies: the chat panel renders only `text` and hides
// `reasoning`/`tool`; the sessions tab reveals `reasoning`/`tool` behind a collapsible `<details>`.
// One conversation projects to a flat, oldest-first array of rows — a `user` row per turn's
// `user-instruction`, an `assistant` row per turn carrying every
// `assistant-text`/`reasoning`/`tool-call`/`tool-result` event as an ordered, typed part — rather
// than the nested "turn { user, assistant }" shape a naive reading of "per turn" might suggest: a
// flat array is what both `ThreadHistoryAdapter.load()` (U4) and a plain list renderer want, and
// turn boundaries are still recoverable from consecutive `runId`s.

// The turn's visible narration, one part per `assistant-text` session event (not pre-joined — a
// renderer that wants one block can still concatenate `text` parts, but the typed projection never
// loses the event boundary).
export const MessagePartText = Schema.TaggedStruct("text", {
  text: Schema.String,
});
export type MessagePartText = typeof MessagePartText.Type;

// The agent's extended thinking, one part per `reasoning` session event. Hidden by the chat-panel
// renderer, revealed (collapsible) by the sessions-tab renderer — this is the part kind D7's
// "reasoning hidden up front, collapsible in sessions" split turns on.
export const MessagePartReasoning = Schema.TaggedStruct("reasoning", {
  text: Schema.String,
});
export type MessagePartReasoning = typeof MessagePartReasoning.Type;

// A tool-activity heartbeat, one part per `tool-call`/`tool-result` session event — mirrors
// `AgentTurnEvent`'s live-stream `ToolActivity` shape (`name` + `phase`) so the two "what tool ran"
// projections (live wire vs. durable history) read as one vocabulary. `tool-result` session events
// carry no tool name of their own (only `toolUseId`); the projection recovers it from the run's own
// `tool-call` events by `toolUseId`, the same recovery `agent-service.ts`'s `rememberToolName` does
// for the live stream. `argsText` mirrors `session.ts`'s `ToolCall.input` (the tool's arguments,
// already JSON-stringified and truncated by the adapter) — present only on a `phase:"call"` part;
// `result` mirrors `ToolResult.result` (the flattened, truncated result text) — present only on a
// `phase:"result"` part. Both are absent (never an empty string) when the source session event
// carried nothing, so the sessions-tab renderer can tell "no detail recorded" from "empty detail".
export const MessagePartTool = Schema.TaggedStruct("tool", {
  name: Schema.String,
  phase: Schema.Literal("call", "result"),
  argsText: Schema.optional(Schema.String),
  result: Schema.optional(Schema.String),
});
export type MessagePartTool = typeof MessagePartTool.Type;

export const ConversationMessagePart = Schema.Union(
  MessagePartText,
  MessagePartReasoning,
  MessagePartTool,
);
export type ConversationMessagePart = typeof ConversationMessagePart.Type;

// The Expert's turn-starting instruction, one row per turn's `user-instruction` session event.
export const ConversationUserMessage = Schema.TaggedStruct("user", {
  runId: RunId,
  at: Schema.DateTimeUtc,
  text: Schema.String,
});
export type ConversationUserMessage = typeof ConversationUserMessage.Type;

// The turn's assistant side, one row per turn carrying every part the run produced, in the order the
// run recorded them. `at` is the run's last event time (the turn's overall settle point), not any one
// part's own timestamp — parts don't carry timestamps of their own.
export const ConversationAssistantMessage = Schema.TaggedStruct("assistant", {
  runId: RunId,
  at: Schema.DateTimeUtc,
  parts: Schema.Array(ConversationMessagePart),
});
export type ConversationAssistantMessage = typeof ConversationAssistantMessage.Type;

export const ConversationMessage = Schema.Union(
  ConversationUserMessage,
  ConversationAssistantMessage,
);
export type ConversationMessage = typeof ConversationMessage.Type;
